import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ControlField, FieldMetadata } from '../../contexts/MetadataContext';
import { FormFieldState, Option } from '../edit/FormFieldContext';
import { CustomControlTypes } from '../../constants/Control';
import { MemoizedFieldContextProps } from '../../pages/edit/components/MemoizedField';
import { FieldPresentation } from '@brightmls/nextgen.lae.flc';

export interface CustomControlProps extends CustomControlImplementation {
  control: string;
  onLoaded: () => void;
}

export interface CustomControlImplementation {
  onFieldValueChange: (value: any) => any;
  handleFieldTouch: (value?: any) => any;
  field: ControlField;
  fieldMeta: FieldMetadata;
  fieldValue: any;
  fieldLabel: string;
  fieldSourceValue: any;
  options?: Option[];
  isTouched: boolean;
  fieldPresentation: FieldPresentation;
  resourceFormContext?: MemoizedFieldContextProps;
  fieldFrame: FormFieldState;
}

const CustomControl: React.FC<CustomControlProps> = ({
  control,
  field,
  fieldMeta,
  fieldValue,
  fieldLabel,
  fieldPresentation,
  options,
  fieldSourceValue,
  isTouched,
  onLoaded,
  onFieldValueChange,
  resourceFormContext = undefined,
  handleFieldTouch,
  fieldFrame,
}) => {
  const [ControlComponent, setControlComponent] =
    useState<React.ComponentType<any> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Find the control type definition to check its version
    const controlType = CustomControlTypes.find((c) => c.key === control);
    const isV2 = controlType?.version === 'v2';

    // Use different import paths for v1 and v2 controls
    const importAction = isV2
      ? import(`../edit/control/${control}/index`)
      : import(`../edit/control/${control}`);

    importAction
      .then((module) => {
        setControlComponent(() => module.default);
        onLoaded();
      })
      .catch((err) => {
        setError(`No Control Found '${control}'`);
        onLoaded();
      });
  }, [control, onLoaded]);

  if (error) {
    return (
      <Box
        sx={{
          p: 2,
          border: '1px solid #f0f0f0',
          borderRadius: 1,
          bgcolor: '#fff9c4',
          color: '#666',
        }}
      >
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  if (!ControlComponent) {
    return <CircularProgress size={20} />;
  }

  return (
    <ControlComponent
      field={field}
      fieldLabel={fieldLabel}
      fieldMeta={fieldMeta}
      fieldPresentation={fieldPresentation}
      fieldValue={fieldValue}
      fieldSourceValue={fieldSourceValue}
      isTouched={isTouched}
      onFieldValueChange={onFieldValueChange}
      handleFieldTouch={handleFieldTouch}
      options={options}
      {...(resourceFormContext ? { resourceFormContext } : {})}
      fieldFrame={fieldFrame}
    />
  );
};

export default React.memo(CustomControl);
